import { useState } from 'react';
import type { SigningRequestSummary, SignerSummary } from '@corner/shared';
import { APP } from './tokens';

interface SigningStatusPanelProps {
  signingRequest: SigningRequestSummary;
  currentUserEmail?: string;
  onSendReminder?: (signerEmail: string) => void;
  onResendLink?: (signerEmail: string) => void;
  onRevoke?: () => void;
  onDownload?: () => void;
  onDownloadDocx?: () => void;
  onExportToDrive?: () => void;
  onArchive?: () => void;
  onRefresh?: () => void;
  onBack?: () => void;
}

export default function SigningStatusPanel({
  signingRequest,
  currentUserEmail,
  onSendReminder,
  onResendLink,
  onRevoke,
  onDownload,
  onDownloadDocx,
  onExportToDrive,
  onArchive,
  onRefresh,
  onBack,
}: SigningStatusPanelProps) {
  const { signedCount, totalCount, status, signers, documentName: _documentName } = signingRequest;
  const isComplete = status === 'completed';
  const isRevoked = status === 'revoked';

  const formatTime = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' }) +
      ' · ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  // Find pending signers for action buttons
  const pendingSigners = signers.filter((s) => s.status !== 'signed');

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      fontFamily: 'Geist, system-ui, sans-serif',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid #E8DED4',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back to signatures"
              title="Back to signatures"
              style={{
                width: 28,
                height: 28,
                borderRadius: 7,
                border: '1px solid #E8DED4',
                backgroundColor: '#FAFAF8',
                color: '#6B5E52',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5" />
                <path d="m12 19-7-7 7-7" />
              </svg>
            </button>
          )}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="1.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
            <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 00-3-3.87" />
            <path d="M16 3.13a4 4 0 010 7.75" />
          </svg>
          <span style={{ fontSize: 14, fontWeight: 600, color: APP.textCardBody, lineHeight: '18px' }}>Signing Status</span>
        </div>
        <button
          onClick={onRefresh}
          style={{
            border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: '#9E9189',
            display: 'flex', alignItems: 'center', flexShrink: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </div>

      {/* Progress section */}
      <div style={{
        padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10,
        borderBottom: '1px solid #E8DED4', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: APP.textCardBody, lineHeight: '16px' }}>
            {signedCount} of {totalCount} signed
          </span>
          <div style={{
            display: 'flex', alignItems: 'center', borderRadius: 12,
            padding: '3px 10px', gap: 4,
            backgroundColor: isComplete ? '#E8F5ED' : isRevoked ? '#FFEBEE' : '#FDF5EF',
          }}>
            <span style={{
              fontSize: 11, fontWeight: 600, lineHeight: '14px',
              color: isComplete ? '#1B7A4B' : isRevoked ? '#C62828' : '#D97645',
            }}>
              {isComplete ? 'Complete' : isRevoked ? 'Revoked' : 'In progress'}
            </span>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{ display: 'flex', width: '100%', height: 4, borderRadius: 2, backgroundColor: '#E8DED4', flexShrink: 0 }}>
          <div style={{
            width: `${totalCount > 0 ? (signedCount / totalCount) * 100 : 0}%`,
            height: 4, borderRadius: 2,
            backgroundColor: isComplete ? '#1B7A4B' : '#D97645',
            transition: 'width 0.3s ease',
          }} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 400, color: '#9E9189', lineHeight: '14px' }}>
          {isComplete ? `Completed ${formatDate(signingRequest.completedAt || signingRequest.createdAt)}` : `Sent ${formatDate(signingRequest.createdAt)}`}
        </span>
      </div>

      {/* Signers list */}
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1, overflowY: 'auto' }}>
        <span style={{
          fontSize: 11, fontWeight: 400, textTransform: 'uppercase' as const, letterSpacing: '0.04em',
          color: '#9E9189', lineHeight: '14px', paddingBottom: 6,
        }}>
          Signers
        </span>
        {signers.map((signer, i) => (
          <SignerRow
            key={i}
            signer={signer}
            isSelf={signer.email === currentUserEmail}
            formatTime={formatTime}
          />
        ))}
      </div>

      {/* Action buttons */}
      {!isComplete && !isRevoked && (
        <div style={{
          padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8,
          borderTop: '1px solid #E8DED4', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {pendingSigners.length > 0 && onSendReminder && (
              <button
                onClick={() => onSendReminder(pendingSigners[0].email)}
                style={{
                  flex: 1, border: 'none', borderRadius: 6, padding: '8px 12px',
                  backgroundColor: APP.textDark, color: '#FDFCFB', fontSize: 12, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'Geist, system-ui, sans-serif', lineHeight: '16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FDFCFB" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
                  <path d="M22 2L11 13" />
                  <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
                Send Reminder
              </button>
            )}
            {pendingSigners.length > 0 && onResendLink && (
              <button
                onClick={() => onResendLink(pendingSigners[0].email)}
                style={{
                  flex: 1, border: '1px solid #E8DED4', borderRadius: 6, padding: '8px 12px',
                  backgroundColor: 'transparent', color: APP.textCardBody, fontSize: 12, fontWeight: 400,
                  cursor: 'pointer', fontFamily: 'Geist, system-ui, sans-serif', lineHeight: '16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                Resend Link
              </button>
            )}
          </div>
          {onRevoke && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <button
                onClick={onRevoke}
                style={{
                  border: 'none', background: 'none', cursor: 'pointer',
                  fontSize: 11, fontWeight: 400, color: '#B5AA9C', lineHeight: '14px',
                  fontFamily: 'Geist, system-ui, sans-serif',
                }}
              >
                Revoke invite
              </button>
            </div>
          )}
        </div>
      )}

      {/* Complete actions */}
      {isComplete && (
        <div style={{
          padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10,
          borderTop: '1px solid #E8DED4', flexShrink: 0,
        }}>
          {onDownload && (
            <button
              onClick={onDownload}
              style={{
                width: '100%', border: 'none', borderRadius: 8, padding: '10px 20px',
                backgroundColor: APP.textDark, color: '#FDFCFB', fontSize: 13, fontWeight: 500,
                cursor: 'pointer', fontFamily: 'Geist, system-ui, sans-serif', lineHeight: '16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FDFCFB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download Signed PDF
            </button>
          )}
          {onDownloadDocx && (
            <>
              <button
                type="button"
                onClick={onDownloadDocx}
                style={{
                  width: '100%', border: '1px solid #E8DED4', borderRadius: 8, padding: '10px 20px',
                  backgroundColor: '#FAFAF8', color: APP.textCardBody, fontSize: 13, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'Geist, system-ui, sans-serif', lineHeight: '16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                Download Word (.docx)
              </button>
              <p style={{ margin: 0, fontSize: 11, lineHeight: '15px', color: '#9E9189', textAlign: 'center' }}>
                Text extracted from the signed PDF for editing — layout and handwriting may differ from the PDF.
              </p>
            </>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {onExportToDrive && (
              <button
                onClick={onExportToDrive}
                style={{
                  flex: 1, border: '1px solid #E8DED4', borderRadius: 6, padding: '8px 12px',
                  backgroundColor: 'transparent', color: APP.textCardBody, fontSize: 12, fontWeight: 400,
                  cursor: 'pointer', fontFamily: 'Geist, system-ui, sans-serif', lineHeight: '16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                }}
              >
                Export to Drive
              </button>
            )}
            {onArchive && (
              <button
                onClick={onArchive}
                style={{
                  flex: 1, border: '1px solid #E8DED4', borderRadius: 6, padding: '8px 12px',
                  backgroundColor: 'transparent', color: APP.textCardBody, fontSize: 12, fontWeight: 400,
                  cursor: 'pointer', fontFamily: 'Geist, system-ui, sans-serif', lineHeight: '16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                }}
              >
                Archive
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SignerRow({ signer, isSelf, formatTime }: { signer: SignerSummary; isSelf: boolean; formatTime: (iso?: string) => string }) {
  const [expanded, setExpanded] = useState(false);
  const avatarColor = isSelf ? '#D97645' : '#8B7D6B';
  const initials = signer.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);

  const isSigned = signer.status === 'signed';
  const isViewed = signer.status === 'viewed';
  const emailStatus = signer.emailStatus ?? 'pending';
  const emailLabel =
    emailStatus === 'sent'
      ? 'Invite sent'
      : emailStatus === 'failed'
        ? 'Invite failed'
        : 'Invite pending';
  const emailColor =
    emailStatus === 'sent'
      ? '#1B7A4B'
      : emailStatus === 'failed'
        ? '#C62828'
        : '#9E9189';

  // Card styling differs by status
  const cardBg = isSigned ? '#F4FAF6' : '#FAFAF8';
  const cardBorder = isSigned ? '#D4E8DA' : '#E8DED4';

  return (
    <button
      type="button"
      onClick={() => setExpanded((next) => !next)}
      aria-expanded={expanded}
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: expanded ? 8 : 0,
        borderRadius: 8,
        padding: 12,
        backgroundColor: cardBg,
        border: `1px solid ${expanded ? '#D97645' : cardBorder}`,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'Geist, system-ui, sans-serif',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
        {/* Avatar */}
        <div style={{
          width: 32, height: 32, borderRadius: 16, backgroundColor: avatarColor, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 600, color: '#FFFFFF', lineHeight: '16px',
        }}>
          {initials}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: APP.textCardBody, lineHeight: '16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {signer.name}
            </span>
            {isSelf && (
              <span style={{
                display: 'inline-block', fontSize: 10, fontWeight: 400, padding: '1px 6px',
                borderRadius: 3, backgroundColor: '#F0ECE6', color: '#9E9189', lineHeight: '12px',
              }}>
                You
              </span>
            )}
          </div>
          <span style={{ fontSize: 11, fontWeight: 400, color: '#9E9189', lineHeight: '14px' }}>
            {signer.role}
          </span>
          <span style={{ fontSize: 10, fontWeight: 600, color: emailColor, lineHeight: '13px' }}>
            {emailLabel}
          </span>
        </div>

        {/* Status + time */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {isSigned && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1B7A4B" strokeWidth="3" strokeLinecap="round" style={{ flexShrink: 0 }}>
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
            {isViewed && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="2" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4" />
                <path d="M12 16h.01" />
              </svg>
            )}
            <span style={{
              fontSize: 11, fontWeight: 500, lineHeight: '14px',
              color: isSigned ? '#1B7A4B' : isViewed ? '#D97645' : '#9E9189',
            }}>
              {isSigned ? 'Signed' : isViewed ? 'Viewed' : 'Pending'}
            </span>
          </div>
          {(signer.signedAt || signer.viewedAt) && (
            <span style={{ fontSize: 10, fontWeight: 400, color: '#B5AA9C', lineHeight: '12px' }}>
              {formatTime(signer.signedAt || signer.viewedAt)}
            </span>
          )}
        </div>
      </div>
      {expanded && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          paddingTop: 8,
          borderTop: '1px solid #E8DED4',
          width: '100%',
        }}>
          <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#B5AA9C', lineHeight: '12px' }}>
            Invite email
          </span>
          <span style={{ fontSize: 12, fontWeight: 500, color: APP.textCardBody, lineHeight: '16px', wordBreak: 'break-all' }}>
            {signer.email}
          </span>
          <span style={{ fontSize: 11, fontWeight: 400, color: emailColor, lineHeight: '15px' }}>
            Delivery status: {emailLabel}
            {signer.lastReminderSentAt ? ` · Last reminder ${formatTime(signer.lastReminderSentAt)}` : ''}
          </span>
        </div>
      )}
    </button>
  );
}
